import React, { useState } from 'react';
import { Modal, Button, ListGroup, Badge, Form } from 'react-bootstrap';
// removed unused AssociateCapid import
import { unassociateCapid, associateCapid } from '../api/api';

const readableKey = (k) => {
  // simple human-friendly names for permission keys
  const map = {
    account_active: 'Account Active',
    is_admin: 'Administrator',
    can_edit_inventory: 'Edit Inventory',
    can_edit_compartments: 'Edit Compartments',
    can_edit_items: 'Edit Items'
  };
  return map[k] || k;
};

const booleanBadge = (val) => (
  val
    ? <Badge bg="success">Yes</Badge>
    : <Badge bg="secondary">No</Badge>
);

const ProfileModal = ({ show, onHide, user, onLogout }) => {
  // ensure user may be null without throwing
  const u = user || {};
  const permissions = u.permissions || {};
  const [processingUnlink, setProcessingUnlink] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);

  // Inline CAPID editing state (replaces the separate Link modal)
  const [editingCapid, setEditingCapid] = useState(false);
  const [editedCapidValue, setEditedCapidValue] = useState('');
  const [processingSave, setProcessingSave] = useState(false);

  const handleUnlink = async () => {
    // confirm with the user
    const ok = window.confirm('Are you sure you want to unlink your CAPID?');
    if (!ok) return;
    try {
      setProcessingUnlink(true);
      await unassociateCapid();
      // basic feedback - caller can refresh user state as needed after modal closes
      alert('CAPID unlinked successfully.');
      onHide && onHide();
    } catch (err) {
      console.error('Failed to unassociate CAPID:', err);
      alert('Failed to unlink CAPID. Please try again.');
    } finally {
      setProcessingUnlink(false);
    }
  };

  const openVerifyWindow = () => {
    setShowVerifyModal(true);
  };

  // Save (associate) the edited CAPID inline
  const handleSaveCapid = async () => {
    if (!editedCapidValue || !editedCapidValue.trim()) {
      alert('Please enter a CAPID.');
      return;
    }
    try {
      setProcessingSave(true);
      await associateCapid(editedCapidValue.trim());
      alert('CAPID associated successfully.');
      setEditingCapid(false);
      onHide && onHide(); // caller can refresh user state after modal closes
    } catch (err) {
      console.error('Failed to associate CAPID:', err);
      alert('Failed to save CAPID. Please try again.');
    } finally {
      setProcessingSave(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title>Profile</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <strong>Email:</strong> {u.email || '—'}
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>Username:</strong> {u.user_name || '—'}
            </ListGroup.Item>

            <ListGroup.Item>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <strong className="me-2">CAPID:</strong>
                  {!editingCapid ? (
                    u.capid_associated || '—'
                  ) : (
                    <Form.Control
                      style={{ width: 220 }}
                      value={editedCapidValue}
                      onChange={(e) => setEditedCapidValue(e.target.value)}
                      disabled={processingSave}
                    />
                  )}
                </div>
                <div className="d-flex align-items-center">
                  {!editingCapid ? (
                    <>
                      {u.capid_associated && (u.capid_verified
                        ? <Badge bg="success" className="me-2">Verified</Badge>
                        : <Badge bg="warning" className="me-2">Unverified</Badge>
                      )}
                      {!u.capid_verified && u.capid_associated ? (
                        <Button variant="link" onClick={openVerifyWindow}>How to verify</Button>
                      ) : null}
                      {u.capid_associated ? (
                        <>
                          <Button variant="link" disabled={processingUnlink} onClick={handleUnlink}>Unlink</Button>
                        </>
                      ) : (
                        <Button variant="primary" onClick={() => { setEditingCapid(true); setEditedCapidValue(''); }}>Add CAPID</Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Button variant="primary" className="ms-2" onClick={handleSaveCapid} disabled={processingSave}>
                        {processingSave ? 'Saving...' : 'Save'}
                      </Button>
                      <Button variant="secondary" className="ms-2" onClick={() => { setEditingCapid(false); setEditedCapidValue(''); }} disabled={processingSave}>
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </ListGroup.Item>

            <ListGroup.Item>
              <strong>Permissions</strong>
              <ListGroup className="mt-2">
                {Object.keys(permissions).length === 0 && (
                  <ListGroup.Item className="d-flex justify-content-between">
                    No permissions present
                  </ListGroup.Item>
                )}
                {Object.entries(permissions).map(([k, v]) => (
                  <ListGroup.Item key={k} className="d-flex justify-content-between align-items-center">
                    <span>{readableKey(k)}</span>
                    <span>{booleanBadge(!!v)}</span>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </ListGroup.Item>
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          {onLogout && (
            <Button variant="outline-danger" onClick={() => { onLogout(); onHide(); }}>
              Logout
            </Button>
          )}
          <Button variant="secondary" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* In-app verification modal */}
      <Modal show={showVerifyModal} onHide={() => setShowVerifyModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>How to verify your CAPID</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p><b>Note that you only need to do this if you need write access to the inventory.</b></p>
          <p>Follow these example steps to verify your CAPID:</p>
          <ol>
            <li>Log into EServices and verify the primary email associated with your CAPID.</li>
            <li><b>Using that email address associated with your CAPID,</b> send the following email to <a href="mailto:connor.mcgrath@azwg.cap.gov?subject=Airman's Attic CAPID Link Reuqest">connor.mcgrath@azwg.cap.gov</a> with the subject line "Airman's Attic CAPID Link Reuqest":</li>
          </ol>
          <pre style={{background:'#f4f4f4', padding:10, borderRadius:4}}>
            <p>Please link my Google account with my CAPID:</p>
            <p>Name: {u.user_name}</p>
            <p>Google Email: {u.email}</p>
            <p>User ID: {u.user_id}</p>
            <p>CAPID: {u.capid_associated}</p>
            </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVerifyModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProfileModal;
